import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  PaymentMethod,
  UnregisteredClientId,
} from '../../../../../types/api/orders';
import { LabelValue } from '../../../../../types/options';
import { Unpacked } from '../../../../../types/util';
import { currencyFormat } from '../../../../../utils/helpers/formatting';
import Step from '../../../../Stepper/Inline/Step';
import { FormFields } from '../CreateEditRecreate.functions';

function Payments(): JSX.Element {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const values = watch();

  const { fields, update } = useFieldArray<FormFields, 'payments'>({
    name: 'payments',
  });

  const paymentMethodOptions = useMemo<LabelValue<PaymentMethod>[]>(
    () => [
      { value: PaymentMethod.Cash, label: t('Cash') },
      { value: PaymentMethod.Invoice, label: t('Invoice') },
    ],
    [t]
  );

  const paymentCustomerOptions = useMemo(
    () => [
      {
        value: 1,
        label: t('Sender pays'),
      },
      {
        value: 2,
        label: t('Recipient pays'),
      },
      ...(values?.customer_role === 'orderer'
        ? [
            {
              value: 0,
              label: t('Orderer pays'),
            },
          ]
        : []),
    ],
    [values?.customer_role, t]
  );

  function onPaymentChange<F extends keyof Unpacked<FormFields['payments']>>(
    idx: number,
    field: F,
    value: Unpacked<FormFields['payments']>[F]
  ) {
    update(idx, {
      ...fields[idx],
      [field]: value,
    });
  }

  return (
    <Step title={t('Payments')} fullWidth>
      {!fields.length ? (
        <div
          className="subtitle p-text-center"
          style={{ marginBottom: '15px' }}
        >
          {t("An error occured while calculating order's payments")}
        </div>
      ) : (
        <div className="payments">
          {fields.map((payment, idx) => {
            const id = `payment_${payment.id}`;
            // Payments don't always include an ID,
            //  hence the need to rely on combination of multiple properties.
            const key = `${payment.usluga_id}-${payment.opis}-${payment.iznos}-${payment.ediznos}-${payment.kolicina}`;

            const checkKlientOd = // checking sender to allow for invoice paying
              values?.klient_od_id === fields[idx].klient_id &&
              (fields[idx].klient_id === UnregisteredClientId.Sender ||
                values?.senderInvoice === 0);

            const checkKlientDo = // checking recipient to allow for invoice paying
              values?.klient_do_id === fields[idx].klient_id &&
              (fields[idx].klient_id === UnregisteredClientId.Recipient ||
                values?.recipientInvoice === 0);

            return (
              <div className="p-mb-3" key={key}>
                <Tooltip target={`#${id}`} content={payment.opis} />

                <p className="p-mb-1">
                  <span id={id} className="p-pr-3">
                    {payment.usluga_ime}

                    <span
                      className="p-ml-1 p-text-secondary"
                      style={{ fontSize: 12, color: 'var(--surface-400)' }}
                    >
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </span>
                  </span>
                </p>

                <div className="p-grid">
                  <div className="p-col p-sm-12 p-md-3">
                    <InputText
                      value={currencyFormat(payment.iznos!, {
                        showCurrency: true,
                      })}
                      disabled
                    />
                  </div>

                  <div className="p-col p-sm-12 p-md-4">
                    <Dropdown
                      value={fields[idx]?.gotovina}
                      onChange={(e) =>
                        onPaymentChange(idx, 'gotovina', e.value)
                      }
                      options={paymentMethodOptions}
                      disabled={checkKlientDo || checkKlientOd}
                    />
                  </div>

                  <div className="p-col p-sm-12 p-md-5">
                    <Dropdown
                      value={fields[idx]?.payer}
                      onChange={(e) => onPaymentChange(idx, 'payer', e.value)}
                      options={paymentCustomerOptions}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Step>
  );
}

export default Payments;
